// =============================================================================
// Zakładka Drukarki — kartoteka `print_printers` (odczyt RLS, zapisy przez
// print-core). Naprawia z konstrukcji wady wzorca PackFlow: poprawny słownik
// typów (network/system/virtual zamiast thermal/laser/inkjet), status POCHODNY
// zamiast statycznego i prawdziwy wydruk testowy przez kolejkę zamiast stuba.
//
// Modal dodawania ma DWIE JAWNE ścieżki, bo to dwa różne światy fizyczne:
// drukarka sieciowa (IP/port/protokół — bajty lecą na TCP:9100) i systemowa
// (sterownik na maszynie agenta — dokładna nazwa kolejki spoolera). Ścieżka
// systemowa preferuje wpisy zgłoszone przez agenta (discovery, `source='agent'`,
// `is_active=false`), a do czasu discovery oferuje pole ręczne z ostrzeżeniem.
// =============================================================================

import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  ModalFooter,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from '@insolitum/ui';
import { Pencil, Plus, Power, Printer, RefreshCw, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ActionMenu, ActionMenuItem, ActionMenuSeparator } from '../../../components/ActionMenu';
import { useAuth } from '../../../providers/AuthProvider';

import {
  KOMUNIKAT_BRAK_PRINT_CORE,
  komunikatLimituPlanu,
  PrintCoreError,
  aktualizujDrukarke,
  printCoreDostepny,
  usunDrukarke,
  utworzDrukarke,
  utworzZadanie,
} from './printCoreApi';
import { PROTOKOLY, stanDrukarki, wygladaNaTermiczna } from './status';
import { useAgents, usePrinters, type DrukarkaWiersz } from './usePrintingData';
import { slugStronyTestowej, zmienneStronyTestowej } from './zmienneWydruku';

const ETYKIETY_TYPU: Record<string, string> = {
  network: 'sieciowa',
  system: 'systemowa',
  virtual: 'wirtualna',
};

interface Props {
  /** Filtr ?site= — kartoteka dostanie `site_id` w Fazie 7 (migracja M7). */
  siteId: string | null;
}

type TrybModalu =
  | { tryb: 'nowa' }
  | { tryb: 'edycja'; drukarka: DrukarkaWiersz }
  | { tryb: 'aktywacja'; drukarka: DrukarkaWiersz };

export function PrintersTab(_props: Props) {
  const { t } = useTranslation();
  const { currentOrganization, hasRole } = useAuth();
  const orgId = currentOrganization?.organization_id;
  const admin = hasRole(['owner', 'admin']);
  const zapisMozliwy = printCoreDostepny();

  const { drukarki, raporty, loading, blad, odswiez } = usePrinters();
  const { agenci } = useAgents();

  const [modal, setModal] = useState<TrybModalu | null>(null);
  const [sukces, setSukces] = useState<ReactNode | null>(null);
  const [bladAkcji, setBladAkcji] = useState<string | null>(null);
  const [wysylaTest, setWysylaTest] = useState<string | null>(null);

  const agentPoId = useMemo(() => {
    const mapa = new Map<string, (typeof agenci)[number]>();
    for (const a of agenci) mapa.set(a.id, a);
    return mapa;
  }, [agenci]);

  const wykonaj = async (akcja: () => Promise<void>) => {
    setBladAkcji(null);
    setSukces(null);
    try {
      await akcja();
      await odswiez();
    } catch (err) {
      setBladAkcji(
        komunikatLimituPlanu(err) ??
          (err instanceof PrintCoreError ? err.message : 'Operacja nie powiodła się')
      );
    }
  };

  /**
   * Wydruk testowy = PRAWDZIWE zadanie przez prawdziwy pipeline (plan §5.5):
   * strona testowa z katalogu DOBRANA DO DRUKARKI (A4 przez sterownik, paragon
   * dla ESC/POS, etykieta dla ZPL/EPL/TSPL), jawna drukarka, idempotencja na
   * wypadek podwójnego kliknięcia. Wynik ogląda się w Kolejce — bez udawanego
   * sukcesu. Zmienne wzoru są WYPEŁNIONE (`printed_at`, `printer_name`): do
   * 10.09.2026 szło tu `{}` i bramka pustego payloadu słusznie odbijała każde
   * kliknięcie 422-ką `missing_variable` — patrz `zmienneWydruku.ts`.
   */
  const wydrukTestowy = async (drukarka: DrukarkaWiersz) => {
    if (!orgId) return;
    const slug = slugStronyTestowej(drukarka);
    if (!slug) {
      // `RAW`/brak protokołu nie wskazuje enkodera — plan wzoru nie renderuje na
      // taką drukarkę (print-core odpowiedziałby 422 `format_mismatch`). Mówimy
      // to przed wysyłką, zamiast pokazywać cudzy komunikat po niej.
      setBladAkcji(
        `Drukarka „${drukarka.name}" nie ma protokołu — ustaw ZPL/EPL/TSPL/ESCPOS albo PDF, żeby zlecić stronę testową`
      );
      return;
    }
    setWysylaTest(drukarka.id);
    setBladAkcji(null);
    setSukces(null);
    try {
      const zadanie = await utworzZadanie(orgId, {
        // email-slug-dynamic: slug wzoru WYDRUKU z rodziny `system`, dobrany do drukarki
        template_slug: slug,
        variables: zmienneStronyTestowej(drukarka),
        printer_id: drukarka.id,
        copies: 1,
        client_job_key: crypto.randomUUID(),
      });
      setSukces(
        <span>
          {t('printing.testEnqueued', 'Wydruk testowy zlecony.')}{' '}
          <Link to={`/admin/printing?tab=jobs&job=${zadanie.id}`} className="underline font-medium">
            {t('printing.seeInQueue', 'Zobacz w kolejce')}
          </Link>
        </span>
      );
    } catch (err) {
      setBladAkcji(
        err instanceof PrintCoreError ? err.message : 'Nie udało się zlecić wydruku testowego'
      );
    } finally {
      setWysylaTest(null);
    }
  };

  const przelaczAktywnosc = (drukarka: DrukarkaWiersz) =>
    wykonaj(async () => {
      if (!orgId) return;
      await aktualizujDrukarke(orgId, drukarka.id, { is_active: !drukarka.is_active });
    });

  const usun = (drukarka: DrukarkaWiersz) => {
    if (!confirm(`Czy na pewno chcesz usunąć drukarkę „${drukarka.name}"?`)) return;
    void wykonaj(async () => {
      if (!orgId) return;
      await usunDrukarke(orgId, drukarka.id);
    });
  };

  const polaczenie = (d: DrukarkaWiersz): string => {
    if (d.type === 'network') return d.ip ? `${d.ip}:${d.port ?? 9100}` : '—';
    if (d.type === 'system') return `sterownik: ${d.system_name ?? d.driver ?? '—'}`;
    return 'wirtualna (dry-run)';
  };

  const kolumn = 6;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-aurora-muted">
          {t(
            'printing.printersHint',
            'Kartoteka drukarek organizacji — wspólna dla wszystkich modułów.'
          )}
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => void odswiez()} className="iconbtn" title="Odśwież">
            <RefreshCw className="w-5 h-5" />
          </button>
          {admin && (
            <Button
              onClick={() => setModal({ tryb: 'nowa' })}
              disabled={!zapisMozliwy}
              title={zapisMozliwy ? undefined : KOMUNIKAT_BRAK_PRINT_CORE}
            >
              <Plus className="w-4 h-4" />
              {t('printing.addPrinter', 'Dodaj drukarkę')}
            </Button>
          )}
        </div>
      </div>

      {blad && (
        <Alert variant="error">
          {blad}{' '}
          <button onClick={() => void odswiez()} className="underline">
            {t('printing.retry', 'Spróbuj ponownie')}
          </button>
        </Alert>
      )}
      {bladAkcji && (
        <Alert variant="error" dismissible onDismiss={() => setBladAkcji(null)}>
          {bladAkcji}
        </Alert>
      )}
      {sukces && (
        <Alert variant="success" dismissible onDismiss={() => setSukces(null)}>
          {sukces}
        </Alert>
      )}

      <div className="card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('printing.colName', 'Nazwa')}</TableHead>
              <TableHead>{t('printing.colType', 'Typ')}</TableHead>
              <TableHead>{t('printing.colConnection', 'Połączenie')}</TableHead>
              <TableHead>{t('printing.colProtocol', 'Protokół')}</TableHead>
              <TableHead>{t('printing.colStatus', 'Status')}</TableHead>
              <TableHead className="w-12" aria-label="Akcje" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableLoading colSpan={kolumn} rows={4} />
            ) : drukarki.length === 0 ? (
              <TableEmpty
                colSpan={kolumn}
                icon={<Printer className="w-10 h-10" />}
                message={t('printing.noPrinters', 'Brak drukarek')}
                description={t(
                  'printing.noPrintersHint',
                  'Dodaj pierwszą drukarkę albo uruchom serwer wydruku, który zgłosi drukarki systemowe.'
                )}
              />
            ) : (
              drukarki.map((d) => {
                const agent = d.agent_id ? agentPoId.get(d.agent_id) : null;
                const status = stanDrukarki({ drukarka: d, agent, raport: raporty[d.id] ?? null });
                const doZatwierdzenia = d.source === 'agent' && !d.is_active;
                return (
                  <TableRow key={d.id}>
                    <TableCell className="font-medium text-aurora-text">
                      {d.name}
                      {d.is_default && (
                        <span className="ml-2 text-xs text-aurora-subtle">(domyślna)</span>
                      )}
                      {doZatwierdzenia && (
                        <Badge variant="warning" size="sm" className="ml-2">
                          {t('printing.discovered', 'zgłoszona przez agenta')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{ETYKIETY_TYPU[d.type] ?? d.type}</TableCell>
                    <TableCell className="mono text-xs">{polaczenie(d)}</TableCell>
                    <TableCell>
                      {d.protocol ? (
                        <Badge variant="info" size="sm">
                          {d.protocol}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={status.wariant} size="sm" title={status.szczegol}>
                        {status.symbol} {status.etykieta}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <ActionMenu label={`Akcje: ${d.name}`}>
                        <ActionMenuItem
                          icon={<Printer className="w-4 h-4" />}
                          disabled={!zapisMozliwy || !d.is_active || wysylaTest === d.id}
                          onClick={() => void wydrukTestowy(d)}
                        >
                          {zapisMozliwy
                            ? t('printing.testPrint', 'Wydruk testowy')
                            : KOMUNIKAT_BRAK_PRINT_CORE}
                        </ActionMenuItem>
                        {admin && (
                          <>
                            <ActionMenuSeparator />
                            {doZatwierdzenia ? (
                              <ActionMenuItem
                                icon={<Power className="w-4 h-4" />}
                                variant="primary"
                                disabled={!zapisMozliwy}
                                onClick={() => setModal({ tryb: 'aktywacja', drukarka: d })}
                              >
                                {t('printing.activate', 'Aktywuj wpis')}
                              </ActionMenuItem>
                            ) : (
                              <ActionMenuItem
                                icon={<Power className="w-4 h-4" />}
                                disabled={!zapisMozliwy}
                                onClick={() => void przelaczAktywnosc(d)}
                              >
                                {d.is_active
                                  ? t('printing.deactivate', 'Dezaktywuj')
                                  : t('printing.activate2', 'Aktywuj')}
                              </ActionMenuItem>
                            )}
                            <ActionMenuItem
                              icon={<Pencil className="w-4 h-4" />}
                              disabled={!zapisMozliwy}
                              onClick={() => setModal({ tryb: 'edycja', drukarka: d })}
                            >
                              {t('printing.edit', 'Edytuj')}
                            </ActionMenuItem>
                            <ActionMenuItem
                              icon={<Trash2 className="w-4 h-4" />}
                              variant="destructive"
                              disabled={!zapisMozliwy}
                              onClick={() => usun(d)}
                            >
                              {t('printing.delete', 'Usuń')}
                            </ActionMenuItem>
                          </>
                        )}
                      </ActionMenu>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {!loading && drukarki.length === 0 && !blad && (
        <EmptyState
          variant="card"
          icon={<Printer className="w-8 h-8" />}
          title={t('printing.emptyCta', 'Zacznij od serwera wydruku')}
          description={t(
            'printing.emptyCtaHint',
            'Serwer wydruku (zakładka „Serwery druku") łączy chmurę z drukarkami w Twojej sieci.'
          )}
        />
      )}

      {modal && orgId && (
        <FormularzDrukarki
          tryb={modal}
          orgId={orgId}
          drukarkiDiscovery={drukarki.filter(
            (d) => d.source === 'agent' && !d.is_active && d.type === 'system'
          )}
          onZamknij={() => setModal(null)}
          onZapisano={() => {
            setModal(null);
            void odswiez();
          }}
        />
      )}
    </div>
  );
}

// ── Modal dodawania / edycji / aktywacji wpisu discovery ─────────────────────

interface FormularzProps {
  tryb: TrybModalu;
  orgId: string;
  /** Wpisy `source='agent'`, `is_active=false` — kandydaci ścieżki systemowej. */
  drukarkiDiscovery: DrukarkaWiersz[];
  onZamknij: () => void;
  onZapisano: () => void;
}

function FormularzDrukarki({
  tryb,
  orgId,
  drukarkiDiscovery,
  onZamknij,
  onZapisano,
}: FormularzProps) {
  const { t } = useTranslation();
  const edytowana = tryb.tryb !== 'nowa' ? tryb.drukarka : null;

  const [sciezka, setSciezka] = useState<'network' | 'system'>(
    edytowana?.type === 'system' ? 'system' : 'network'
  );
  const [nazwa, setNazwa] = useState(edytowana?.name ?? '');
  const [ip, setIp] = useState(edytowana?.ip ?? '');
  const [port, setPort] = useState(String(edytowana?.port ?? 9100));
  const [protokol, setProtokol] = useState(edytowana?.protocol ?? 'RAW');
  const [format, setFormat] = useState(edytowana?.format ?? '');
  const [nazwaSystemowa, setNazwaSystemowa] = useState(edytowana?.system_name ?? '');
  const [wybranaDiscovery, setWybranaDiscovery] = useState('');
  const [domyslna, setDomyslna] = useState(edytowana?.is_default ?? false);
  const [zapisuje, setZapisuje] = useState(false);
  const [bladFormularza, setBladFormularza] = useState<string | null>(null);

  const aktywacja = tryb.tryb === 'aktywacja';
  const pomocProtokolu = PROTOKOLY.find((p) => p.wartosc === protokol)?.pomoc;
  // Lekcja PackFlow: surowy PDF na drukarce termicznej drukuje śmieci. Nazwa to
  // tylko heurystyka, więc OSTRZEGAMY — nie blokujemy.
  const ostrzezeniePdfTermiczna =
    sciezka === 'network' && protokol === 'PDF' && wygladaNaTermiczna(nazwa);

  const zapisz = async () => {
    setBladFormularza(null);
    if (!nazwa.trim()) {
      setBladFormularza('Nazwa drukarki jest wymagana');
      return;
    }
    setZapisuje(true);
    try {
      if (aktywacja && edytowana) {
        // Aktywacja wpisu discovery: admin nadaje nazwę i włącza — zero
        // ręcznego przepisywania nazwy kolejki (ona już jest w wierszu).
        await aktualizujDrukarke(orgId, edytowana.id, { name: nazwa.trim(), is_active: true });
      } else if (sciezka === 'network') {
        if (!ip.trim()) {
          setBladFormularza('Adres IP jest wymagany dla drukarki sieciowej');
          setZapisuje(false);
          return;
        }
        const cialo = {
          name: nazwa.trim(),
          type: 'network' as const,
          ip: ip.trim(),
          port: Number.parseInt(port, 10) || 9100,
          protocol: protokol,
          format: format.trim() || null,
          is_default: domyslna,
        };
        if (edytowana) await aktualizujDrukarke(orgId, edytowana.id, cialo);
        else await utworzDrukarke(orgId, cialo);
      } else {
        // Ścieżka systemowa: preferencyjnie aktywacja wpisu zgłoszonego przez
        // agenta; ręczna nazwa kolejki tylko dopóki discovery nie działa.
        if (wybranaDiscovery) {
          await aktualizujDrukarke(orgId, wybranaDiscovery, {
            name: nazwa.trim(),
            is_active: true,
          });
        } else {
          if (!nazwaSystemowa.trim()) {
            setBladFormularza('Nazwa kolejki systemowej jest wymagana');
            setZapisuje(false);
            return;
          }
          const cialo = {
            name: nazwa.trim(),
            type: 'system' as const,
            system_name: nazwaSystemowa.trim(),
            is_default: domyslna,
          };
          if (edytowana) await aktualizujDrukarke(orgId, edytowana.id, cialo);
          else await utworzDrukarke(orgId, cialo);
        }
      }
      onZapisano();
    } catch (err) {
      setBladFormularza(
        komunikatLimituPlanu(err) ??
          (err instanceof PrintCoreError ? err.message : 'Nie udało się zapisać drukarki')
      );
    } finally {
      setZapisuje(false);
    }
  };

  return (
    <Modal
      open
      onClose={onZamknij}
      title={
        aktywacja
          ? t('printing.activateTitle', 'Aktywuj drukarkę zgłoszoną przez agenta')
          : edytowana
            ? t('printing.editPrinter', 'Edytuj drukarkę')
            : t('printing.newPrinter', 'Nowa drukarka')
      }
      size="lg"
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onZamknij}>
            {t('printing.cancel', 'Anuluj')}
          </Button>
          <Button onClick={() => void zapisz()} loading={zapisuje}>
            {aktywacja
              ? t('printing.activateSave', 'Aktywuj')
              : edytowana
                ? t('printing.save', 'Zapisz zmiany')
                : t('printing.add', 'Dodaj')}
          </Button>
        </ModalFooter>
      }
    >
      <div className="space-y-4">
        {bladFormularza && <Alert variant="error">{bladFormularza}</Alert>}

        {/* Dwie jawne ścieżki — przełącznik segmentowy, nie ukryta heurystyka. */}
        {!aktywacja && !edytowana && (
          <div className="flex gap-2">
            <Button
              variant={sciezka === 'network' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setSciezka('network')}
            >
              {t('printing.pathNetwork', 'Sieciowa (IP)')}
            </Button>
            <Button
              variant={sciezka === 'system' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setSciezka('system')}
            >
              {t('printing.pathSystem', 'Systemowa (sterownik)')}
            </Button>
          </div>
        )}

        <div className="field">
          <label className="field__label">{t('printing.printerName', 'Nazwa w kartotece')}</label>
          <Input
            value={nazwa}
            onChange={(e) => setNazwa(e.target.value)}
            placeholder="np. XPrinter A6 — pakowanie"
          />
        </div>

        {aktywacja && tryb.tryb === 'aktywacja' && (
          <Alert variant="info">
            {t('printing.activateHint', 'Kolejka systemowa zgłoszona przez agenta:')}{' '}
            <span className="mono">{tryb.drukarka.system_name ?? tryb.drukarka.name}</span>
          </Alert>
        )}

        {!aktywacja && sciezka === 'network' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="field">
                <label className="field__label">IP</label>
                <Input
                  value={ip}
                  onChange={(e) => setIp(e.target.value)}
                  placeholder="192.168.1.136"
                />
              </div>
              <div className="field">
                <label className="field__label">Port</label>
                <Input value={port} onChange={(e) => setPort(e.target.value)} placeholder="9100" />
              </div>
            </div>
            <Select
              label={t('printing.protocol', 'Protokół')}
              value={protokol}
              onChange={(e) => setProtokol(e.target.value)}
              options={PROTOKOLY.map((p) => ({ value: p.wartosc, label: p.etykieta }))}
              helperText={pomocProtokolu}
            />
            {ostrzezeniePdfTermiczna && (
              <Alert variant="warning">
                {t(
                  'printing.pdfThermalWarning',
                  'Nazwa wygląda na drukarkę termiczną, a wybrany protokół to PDF — surowy PDF na drukarce termicznej wydrukuje nieczytelne znaki. Upewnij się, że drukarka ma interpreter PDF.'
                )}
              </Alert>
            )}
            <div className="field">
              <label className="field__label">
                {t('printing.format', 'Format nośnika (opcjonalnie)')}
              </label>
              <Input
                value={format}
                onChange={(e) => setFormat(e.target.value)}
                placeholder="np. A4, A6, 80x50mm"
              />
            </div>
          </>
        )}

        {!aktywacja && sciezka === 'system' && (
          <>
            {drukarkiDiscovery.length > 0 ? (
              <Select
                label={t('printing.discoveredPrinters', 'Drukarki zgłoszone przez agenta')}
                value={wybranaDiscovery}
                onChange={(e) => setWybranaDiscovery(e.target.value)}
                options={[
                  { value: '', label: t('printing.manualEntry', '— wpisz ręcznie —') },
                  ...drukarkiDiscovery.map((d) => ({
                    value: d.id,
                    label: d.system_name ?? d.name,
                  })),
                ]}
                helperText={t(
                  'printing.discoveredHint',
                  'Wybór z listy aktywuje wpis discovery — bez przepisywania nazwy kolejki.'
                )}
              />
            ) : (
              <Alert variant="warning">
                {t(
                  'printing.noDiscovery',
                  'Agent nie zgłosił jeszcze drukarek systemowych. Możesz wpisać dokładną nazwę kolejki ręcznie — literówka oznacza, że zadania będą kończyć się błędem na agencie.'
                )}
              </Alert>
            )}
            {!wybranaDiscovery && (
              <div className="field">
                <label className="field__label">
                  {t('printing.systemName', 'Dokładna nazwa kolejki systemowej')}
                </label>
                <Input
                  value={nazwaSystemowa}
                  onChange={(e) => setNazwaSystemowa(e.target.value)}
                  placeholder="np. HP LaserJet M404 (spooler Windows) / lp0 (CUPS)"
                />
              </div>
            )}
          </>
        )}

        {!aktywacja && (
          <Switch
            label={t('printing.isDefault', 'Drukarka domyślna organizacji')}
            checked={domyslna}
            onChange={(e) => setDomyslna(e.target.checked)}
          />
        )}
      </div>
    </Modal>
  );
}
